import { Box, HStack, Text, VStack } from '@genial/components';
import { Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import React, { useMemo } from 'react';

import { getReportGeneratorTableColumnFlex } from '../../../../constants/table';
import { reportGeneratorTheme } from '../../../../constants/theme';
import { RowActions } from '../shared/row/RowActions';
import { StatusBadge } from '../shared/row/StatusBadge';
import { useReportGeneratorItemActions } from '../shared/row/useReportGeneratorItemActions';

import { ReportGeneratorItemProps, ReportGeneratorItemTableRow } from './table';

export type { ReportGeneratorItemProps } from './table';

const RowDivider: React.FC = () => <Box w="full" h="1px" bg="neutral.lightest" />;

const valueColor = reportGeneratorTheme.table.valueColor;

const ReportGeneratorItemCard: React.FC<Omit<ReportGeneratorItemProps, 'columnFlex'>> = ({
  item,
  onDownload,
}) => {
  const { actionLoading, handleDownload } = useReportGeneratorItemActions({
    item,
    onDownload,
  });

  return (
    <VStack
      space="stack.xxxs"
      mx="inline.xxxs"
      px="inset.xs"
      py="20px"
      bg={reportGeneratorTheme.table.rowBg}
      borderWidth={1}
      borderColor={reportGeneratorTheme.table.borderColor}
      borderRadius={reportGeneratorTheme.table.itemBorderRadius}>
      <HStack alignItems="center" justifyContent="space-between" width="full">
        <StatusBadge
          status={item.status}
          normalizedError={item.normalizedError}
          bgColor={item.statusIcon.bgColor}
          color={item.statusIcon.color}
        />
        <RowActions item={item} actionLoading={actionLoading} onDownload={handleDownload} />
      </HStack>

      <VStack space="stack.quarck" width="full">
        <Text fontSize={Typography['fontsize.md']} color={valueColor} variant="bold">
          {item.documentType}
        </Text>
        {item.documentTypeSecondaryText ? (
          <Text
            fontSize={Typography['fontsize.xs']}
            color={reportGeneratorTheme.table.documentSubtitleColor}>
            {item.documentTypeSecondaryText}
          </Text>
        ) : null}
      </VStack>

      <RowDivider />

      <HStack
        width="full"
        space="stack.quarck"
        alignItems="flex-start"
        justifyContent="space-between">
        <VStack flex={1} space="stack.quarck">
          <Text fontSize={Typography['fontsize.xs']} color="neutral.medium">
            Solicitado em
          </Text>
          <Text fontSize={Typography['fontsize.md']} color={valueColor}>
            {item.requestedAtNormalized}
          </Text>
        </VStack>

        <VStack flex={1} space="stack.quarck" alignItems="flex-end">
          <Text fontSize={Typography['fontsize.xs']} color="neutral.medium" textAlign="right">
            Período selecionado
          </Text>
          <Text fontSize={Typography['fontsize.md']} color={valueColor} textAlign="right">
            {item.selectedPeriodDisplay}
          </Text>
        </VStack>
      </HStack>
    </VStack>
  );
};

export const ReportGeneratorItem: React.FC<
  Omit<ReportGeneratorItemProps, 'columnFlex'> & { columnFlex?: number[] }
> = (props) => {
  const isTableLayout = useBreakpointValue({ base: false, sm: true });

  const columnFlex = useMemo(
    () =>
      props.columnFlex ??
      Array.from({ length: 5 }, (_, index) => getReportGeneratorTableColumnFlex(index)),
    [props.columnFlex]
  );

  if (isTableLayout) {
    return <ReportGeneratorItemTableRow {...props} columnFlex={columnFlex} />;
  }

  return <ReportGeneratorItemCard item={props.item} onDownload={props.onDownload} />;
};
